import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Mail, MessageCircle, Plus, Search, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import type { ChannelProjection, ContactResponse } from '@outreach/contracts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableScroll,
} from '@/components/ui/table';
import { AddContactDialog } from '@/features/contacts/AddContactDialog';
import { ContactDrawer } from '@/features/contacts/ContactDrawer';
import { ImportDialog } from '@/features/imports/ImportDialog';
import { useBulkDeleteContacts, useContacts } from '@/features/contacts/useContacts';
import { api, ApiRequestError } from '@/lib/api';

const PAGE_SIZE = 50;

const STATUS: Record<
  string,
  { variant: 'success' | 'warning' | 'destructive' | 'muted'; label: string }
> = {
  eligible: { variant: 'success', label: 'ok' },
  no_consent: { variant: 'muted', label: 'none' },
  no_destination: { variant: 'muted', label: '—' },
  consent_withdrawn: { variant: 'warning', label: 'withdrawn' },
  suppressed_unsubscribe: { variant: 'destructive', label: 'unsubscribed' },
  suppressed_complaint: { variant: 'destructive', label: 'complaint' },
  suppressed_permanent_bounce: { variant: 'destructive', label: 'bounced' },
  suppressed_manual: { variant: 'destructive', label: 'blocked' },
  invalid_destination: { variant: 'destructive', label: 'invalid' },
};

function ConsentBadge({ label, projection }: { label: string; projection: ChannelProjection }) {
  if (projection.status === 'no_destination') return null;
  const s = STATUS[projection.status] ?? { variant: 'muted' as const, label: projection.status };
  return <Badge variant={s.variant}>{`${label}: ${s.label}`}</Badge>;
}

export function ContactsPage() {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(() => new Set());

  const { data, isLoading, isError } = useContacts({
    page,
    pageSize: PAGE_SIZE,
    q: query || undefined,
  });
  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const selected: ContactResponse | null = items.find((c) => c.id === selectedId) ?? null;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const bulkDelete = useBulkDeleteContacts();

  // Selection is scoped to what the operator can see; reset when the view changes so
  // a stale off-screen selection can't be deleted by surprise. "All matching" mode
  // (Gmail-style) survives paging but not a new search.
  const [allMatching, setAllMatching] = useState(false);
  useEffect(() => {
    setChecked(new Set());
  }, [page, query]);
  useEffect(() => {
    setAllMatching(false);
  }, [query]);

  const isChecked = (id: string) => allMatching || checked.has(id);
  const allOnPageChecked = items.length > 0 && items.every((c) => isChecked(c.id));
  const someOnPageChecked = items.some((c) => isChecked(c.id));
  const selectedCount = allMatching ? total : checked.size;
  const headerRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headerRef.current) headerRef.current.indeterminate = someOnPageChecked && !allOnPageChecked;
  }, [someOnPageChecked, allOnPageChecked]);

  function clearSelection() {
    setAllMatching(false);
    setChecked(new Set());
  }
  function toggleOne(id: string) {
    if (allMatching) {
      // Leaving "all matching": keep this page selected except the row just unticked.
      setAllMatching(false);
      setChecked(new Set(items.map((c) => c.id).filter((x) => x !== id)));
      return;
    }
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAllOnPage() {
    if (allOnPageChecked) {
      clearSelection();
      return;
    }
    setChecked((prev) => new Set([...prev, ...items.map((c) => c.id)]));
  }

  async function deleteSelected() {
    if (selectedCount === 0) return;
    const scope = allMatching && query ? ` matching "${query}"` : '';
    if (
      !window.confirm(
        `Permanently delete ${selectedCount.toLocaleString()} contact${selectedCount === 1 ? '' : 's'}${scope}? Their details are removed; this cannot be undone.`,
      )
    )
      return;
    try {
      const target = allMatching ? { all: true as const, q: query || undefined } : { ids: [...checked] };
      const res = await bulkDelete.mutateAsync({ target, mode: 'erase' });
      clearSelection();
      setPage(1);
      toast.success(`Deleted ${res.deleted.toLocaleString()} contact${res.deleted === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e instanceof ApiRequestError ? e.message : 'Could not delete contacts');
    }
  }

  function channel(c: ContactResponse, ch: 'email' | 'whatsapp') {
    return c.channels.find((p) => p.channel === ch)!;
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{total} contacts</p>
        <div className="flex flex-wrap items-center gap-2">
          <ImportDialog
            trigger={
              <Button variant="outline" size="sm">
                <Upload className="size-4" /> Import
              </Button>
            }
          />
          <Button asChild variant="outline" size="sm">
            <a href={api.exportUrl()}>
              <Download className="size-4" /> Export
            </a>
          </Button>
          <AddContactDialog
            trigger={
              <Button size="sm">
                <Plus className="size-4" /> Add contact
              </Button>
            }
          />
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Search name, email or phone"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
          aria-label="Search contacts"
        />
      </div>

      {selectedCount > 0 && (
        <div className="space-y-2 rounded-lg border bg-muted/40 px-4 py-2.5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm font-medium">
              {allMatching ? `All ${total.toLocaleString()} contacts selected` : `${selectedCount} selected`}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={clearSelection}>
                <X className="size-4" /> Clear
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={deleteSelected}
                disabled={bulkDelete.isPending}
              >
                <Trash2 className="size-4" /> {bulkDelete.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
          {!allMatching && allOnPageChecked && total > items.length && (
            <p className="text-sm text-muted-foreground">
              All {items.length} on this page are selected.{' '}
              <button
                type="button"
                className="font-medium text-primary underline-offset-4 hover:underline"
                onClick={() => setAllMatching(true)}
              >
                Select all {total.toLocaleString()} contacts{query ? ` matching "${query}"` : ''}
              </button>
            </p>
          )}
        </div>
      )}

      <div className="rounded-xl border bg-card">
        {isLoading && <p className="p-6 text-sm text-muted-foreground">Loading contacts…</p>}
        {isError && <p className="p-6 text-sm text-destructive">Could not load contacts.</p>}
        {!isLoading && !isError && items.length === 0 && (
          <div className="flex flex-col items-center gap-2 p-10 text-center">
            <p className="text-sm text-muted-foreground">
              {query
                ? 'No contacts match your search.'
                : 'No contacts yet. Add your first contact.'}
            </p>
          </div>
        )}
        {items.length > 0 && (
          <TableScroll>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input
                      ref={headerRef}
                      type="checkbox"
                      aria-label="Select all on this page"
                      checked={allOnPageChecked}
                      onChange={toggleAllOnPage}
                      className="size-4 cursor-pointer align-middle accent-primary"
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Channels</TableHead>
                  <TableHead>Consent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer"
                    onClick={() => setSelectedId(c.id)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedId(c.id);
                      }
                    }}
                    data-state={isChecked(c.id) ? 'selected' : undefined}
                  >
                    <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${[c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || c.phone || 'contact'}`}
                        checked={isChecked(c.id)}
                        onChange={() => toggleOne(c.id)}
                        className="size-4 cursor-pointer align-middle accent-primary"
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      {[c.firstName, c.lastName].filter(Boolean).join(' ') || '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        {c.email && (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Mail className="size-3" /> {c.email}
                          </span>
                        )}
                        {c.phone && (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <MessageCircle className="size-3" /> {c.phone}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1.5">
                        <ConsentBadge label="Email" projection={channel(c, 'email')} />
                        <ConsentBadge label="WA" projection={channel(c, 'whatsapp')} />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableScroll>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <ContactDrawer contact={selected} onClose={() => setSelectedId(null)} />
    </div>
  );
}
